import { io, type Socket } from 'socket.io-client';
import type { Admission, Transfer, Snapshot, StateBatch, InputState, PlayerState, ActionState, Reply } from '../../../../vrroom_typescript/src/platform/contracts';

/**
 * crypto.randomUUID 只在安全上下文（HTTPS 或 localhost）暴露，公网 HTTP 部署下它是
 * undefined。crypto.getRandomValues 不受该限制，因此回退到手工拼装的 UUID v4。
 * 服务端只校验 requestId 是 1–100 字符的字符串（server.ts:23），不要求 UUID 格式。
 */
function requestId(): string {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * 加密房间的密码输入弹窗（屏幕居中，蓝玻璃风格与场景内 wm-modal 系列一致）。
 * 替代原来的 window.prompt：返回 Promise<string>，用户取消时 reject。
 */
function askRoomPassword(invalid: boolean): Promise<string> {
    return new Promise((resolve, reject) => {
        const overlay = document.createElement('div');
        overlay.id = 'room-password-overlay';
        const card = document.createElement('div');
        card.className = 'rp-card' + (invalid ? ' rp-invalid' : '');
        const icon = document.createElement('div');
        icon.className = 'rp-icon';
        icon.textContent = '🔒';
        const title = document.createElement('h3');
        title.textContent = '进入加密房间';
        const hint = document.createElement('p');
        hint.className = 'rp-hint';
        hint.textContent = invalid ? '密码不正确，请重新输入房间密码' : '该房间已加密，请输入房主发布的房间密码';
        const input = document.createElement('input');
        input.className = 'rp-input';
        input.type = 'password';
        input.maxLength = 64;
        input.autocomplete = 'off';
        input.placeholder = '房间密码';
        const actions = document.createElement('div');
        actions.className = 'rp-actions';
        const cancel = document.createElement('button');
        cancel.className = 'rp-btn rp-cancel';
        cancel.type = 'button';
        cancel.textContent = '取消';
        const ok = document.createElement('button');
        ok.className = 'rp-btn rp-ok';
        ok.type = 'button';
        ok.textContent = '确认进入';
        actions.append(cancel, ok);
        card.append(icon, title, hint, input, actions);
        overlay.append(card);
        (document.getElementById('ui-layer') ?? document.body).append(overlay);
        const done = (value?: string) => {
            overlay.remove();
            document.removeEventListener('keydown', onKey, true);
            if (value === undefined) reject(new Error('已取消进入房间'));
            else resolve(value);
        };
        const submit = () => {
            if (!input.value) { input.focus(); card.classList.add('rp-shake'); window.setTimeout(() => card.classList.remove('rp-shake'), 400); return; }
            done(input.value);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.stopPropagation(); done(); }
            else if (e.key === 'Enter') { e.stopPropagation(); submit(); }
        };
        ok.addEventListener('click', submit);
        cancel.addEventListener('click', () => done());
        overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) done(); });
        document.addEventListener('keydown', onKey, true);
        window.setTimeout(() => input.focus(), 60);
    });
}

export class RoomSession {
    readonly socket: Socket;
    admission?: Admission;
    online = false;
    offset = 0;
    poseRevision = 1;
    private seq = 0;
    private generation = 0;
    private stopped = false;
    private busy = false;
    private acceptStream = false;
    private resumeToken = '';
    private resumeRoomId = '';
    private lastTick = -1;
    private serverEpoch = '';
    private clockTimer?: ReturnType<typeof setInterval>;
    onLoad: (admission: Admission) => Promise<void> = async () => {};
    onSnapshot: (snapshot: Snapshot) => void = () => {};
    onStates: (batch: StateBatch) => void = () => {};
    onEvent: (name: string, data: any) => void = () => {};
    onStatus: (message: string) => void = () => {};
    onReset: (state: PlayerState) => void = () => {};
    constructor(readonly userId: number, private invite: {roomId: string; sceneId: string; invite: string}) {
        // resume 凭据按 {roomId, token} 存储：目标房间与上次所在房间不同（如新建
        // 房间后跳转 /join）时必须丢弃 resume 走 room:join，否则会被拉回旧房间、
        // 绕过新房间的密码校验。旧格式（裸 token）无法比对 roomId，同样丢弃。
        const saved = sessionStorage.getItem(`room-resume:${userId}`);
        let resumeRoomId = '';
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed.roomId === 'string' && typeof parsed.token === 'string' && parsed.roomId === invite.roomId) {
                    resumeRoomId = parsed.roomId;
                    this.resumeToken = parsed.token;
                }
            } catch { /* 裸 token 旧格式：丢弃 */ }
            if (!this.resumeToken) sessionStorage.removeItem(`room-resume:${userId}`);
        }
        this.resumeRoomId = resumeRoomId;
        this.socket = io({ autoConnect: false, transports: ['websocket'], auth: {protocolVersion: 1}, reconnectionDelay: 1000, reconnectionDelayMax: 5000 });
        this.socket.on('disconnect', reason => {
            this.online = false; this.acceptStream = false; this.generation++; this.onStatus('连接中断，正在恢复……');
            if (reason === 'io server disconnect') { this.stopped = true; this.onStatus('会话已结束，请刷新页面重新进入。'); }
        });
        this.socket.on('room:snapshot', data => { if (this.acceptStream) this.snapshot(data); });
        this.socket.on('players:state', (data: StateBatch) => {
            if (!this.matches(data) || data.serverTick <= this.lastTick) return;
            this.lastTick = data.serverTick; this.onStates(data);
        });
        for (const name of ['player:joined', 'player:left', 'player:profile', 'player:action', 'player:muted', 'player:moderator', 'player:mic-open', 'room:objects', 'room:video']) {
            this.socket.on(name, data => { if (this.matches(data)) this.onEvent(name, data); });
        }
        for (const name of ['room:kicked', 'room:closed']) this.socket.on(name, data => {
            this.stopped = true; this.online = false; this.resumeToken = ''; sessionStorage.removeItem(`room-resume:${userId}`); this.onStatus(name === 'room:closed' ? '活动房间已关闭。' : '已离开房间，请重新打开邀请链接。'); this.onEvent(name, data);
        });
        this.socket.on('player:reset', (data: PlayerState) => this.acceptReset(data));
        this.socket.on('room:updated', data => { if (data.room?.id === this.admission?.room.id) this.onEvent('room:updated', data); });
    }
    private matches(data: {roomId: string; serverEpoch: string}) { return this.acceptStream && data.roomId === this.admission?.room.id && data.serverEpoch === this.serverEpoch; }
    private snapshot(data: Snapshot) {
        if (data.room.id !== this.admission?.room.id) return;
        if (this.serverEpoch && data.serverEpoch !== this.serverEpoch) return;
        this.serverEpoch = data.serverEpoch;
        const me = data.players.find(p => p.profile.id === this.userId);
        if (me && me.state.membershipEpoch === this.admission.membershipEpoch) { if (this.online && me.state.poseRevision > this.poseRevision) this.acceptReset(me.state); this.poseRevision = me.state.poseRevision; this.seq = Math.max(this.seq, me.state.seq); }
        this.onSnapshot(data);
    }
    private adopt(admission: Admission) {
        this.admission = admission; this.resumeToken = admission.resumeToken; this.resumeRoomId = admission.room.id; this.seq = 0; this.poseRevision = 1; this.lastTick = -1;
        sessionStorage.setItem(`room-resume:${this.userId}`, JSON.stringify({roomId: admission.room.id, token: admission.resumeToken}));
    }
    async command<T>(name: string, body: Record<string, unknown> = {}): Promise<T> {
        const request = {...body, requestId: requestId()};
        for (let attempt = 0; ; attempt++) {
            if (!this.socket.connected) throw new Error('DISCONNECTED');
            let reply: Reply<T>;
            try { reply = await this.socket.timeout(8000).emitWithAck(name, request); }
            catch (error) { if (attempt < 1 && this.socket.connected) continue; throw error; }
            if (!reply.ok) throw new Error(reply.error.code);
            return reply.data;
        }
    }
    /** 主持人放置房间物体（GLB 摆件）：服务端生成 id 并广播全员
     *  （含本人——客户端统一经 room:objects 事件路径应用，不本地先行）。 */
    async spawnRoomObject(object: Record<string, unknown>): Promise<{ id: string }> {
        return this.command<{ id: string }>('room:object-spawn', { object });
    }
    /** 主持人删除房间物体。 */
    async removeRoomObject(id: string): Promise<void> {
        await this.command('room:object-remove', { id });
    }
    /** 主持人移动物体（Gizmo 拖拽结束上行完整变换，服务端广播全员）。 */
    async moveRoomObject(id: string, transform: {
        position: [number, number, number];
        rotation: [number, number, number];
        scale: [number, number, number];
    }): Promise<void> {
        await this.command('room:object-move', { id, transform });
    }
    /** 主持人视频屏控制（play/pause/seek/show/hide/mute/unmute）：服务端
     *  更新房间内存态并广播 room:video，含本人——全部客户端统一按事件
     *  路径应用，不本地先行（与房间物体同构）。 */
    async videoControl(screenId: string, action: 'play' | 'pause' | 'seek' | 'show' | 'hide' | 'mute' | 'unmute', value?: number): Promise<void> {
        await this.command('room:video-control', { screenId, action, value });
    }
    private async passwordCommand<T>(name: string, body: Record<string, unknown>): Promise<T> {
        for (;;) {
            try { return await this.command<T>(name, body); }
            catch (error) {
                if (!(error instanceof Error) || !['ROOM_PASSWORD_REQUIRED','ROOM_PASSWORD_INVALID'].includes(error.message)) throw error;
                const password = await askRoomPassword(error.message === 'ROOM_PASSWORD_INVALID');
                body.password = password;
            }
        }
    }
    async start(): Promise<void> {
        return new Promise((resolve, reject) => {
            let first = true;
            this.socket.on('connect', async () => {
                if (this.stopped) return;
                const gen = ++this.generation; this.online = false; this.serverEpoch = ''; this.busy = false;
                try {
                    let admission: Admission;
                    if (this.resumeToken) {
                        try {
                            admission = await this.command('room:resume', {resumeToken: this.resumeToken});
                            // 防御：resume 恢复的房间与本次要进的房间不一致时（换了邀请
                            // 链接/新建房间），离开并改走 room:join，确保密码与人数校验。
                            if (admission.room.id !== this.invite.roomId) {
                                this.resumeToken = '';
                                await this.command('room:leave').catch(() => {});
                                admission = await this.passwordCommand('room:join', this.invite);
                            }
                        }
                        catch (e) { if (!(e instanceof Error) || e.message !== 'RESUME_EXPIRED') throw e; this.resumeToken = ''; admission = await this.passwordCommand('room:join', this.invite); }
                    } else admission = await this.passwordCommand('room:join', this.invite);
                    if (gen !== this.generation) return;
                    this.adopt(admission); await this.measureClock(); if (gen !== this.generation) return; await this.onLoad(admission);
                    if (gen !== this.generation) return;
                    const snapshot = await this.command<Snapshot>('player:ready', {membershipEpoch: admission.membershipEpoch});
                    if (gen !== this.generation) return;
                    this.acceptStream = true; this.snapshot(snapshot);
                    this.online = true; this.onStatus(`已连接「${admission.room.name}」`);
                    if (!this.clockTimer) this.clockTimer = setInterval(() => { void this.measureClock(); }, 10000);
                    if (first) { first = false; resolve(); }
                } catch (e) {
                    if (gen !== this.generation) return;
                    this.onStatus(`进入失败：${e instanceof Error ? e.message : e}`);
                    void this.command('room:leave').catch(() => {});
                    if (first) { first = false; reject(e); }
                }
            });
            this.socket.on('connect_error', error => { this.onStatus(`连接失败：${error.message}`); if (['AUTH_REQUIRED','PROTOCOL_MISMATCH'].includes(error.message) && first) { first = false; reject(error); } });
            this.socket.connect();
        });
    }
    private async measureClock() {
        const start = Date.now();
        try { const server: number = await this.socket.timeout(3000).emitWithAck('time:ping'); this.offset = server - (start + Date.now()) / 2; } catch { /* reconnect owns failures */ }
    }
    get serverTime() { return Date.now() + this.offset; }
    publish(state: Omit<InputState, 'membershipEpoch'|'poseRevision'|'seq'>) {
        if (!this.online || !this.admission || this.busy) return;
        this.socket.volatile.emit('player:state', {...state, membershipEpoch: this.admission.membershipEpoch, poseRevision: this.poseRevision, seq: ++this.seq});
    }
    async action(actionId: string | null): Promise<ActionState> {
        if (!this.online) throw new Error('尚未进入房间');
        return this.command('player:action', {actionId, membershipEpoch: this.admission!.membershipEpoch});
    }
    private acceptReset(state: PlayerState) {
        if (state.membershipEpoch !== this.admission?.membershipEpoch || state.poseRevision <= this.poseRevision) return;
        this.poseRevision = state.poseRevision; this.seq = Math.max(this.seq, state.seq); this.onReset(state);
    }
    async reset() { if (this.online) this.acceptReset(await this.command('player:reset', {membershipEpoch: this.admission!.membershipEpoch})); }
    async transfer(portalId: string) {
        if (!this.online || this.busy) return;
        this.busy = true; const gen = this.generation;
        try {
            const target = await this.passwordCommand<Transfer>('portal:prepare', {portalId, membershipEpoch: this.admission!.membershipEpoch});
            if (gen !== this.generation) return;
            this.online = false; this.acceptStream = false;
            await this.onLoad(target);
            if (gen !== this.generation) return;
            const committed = await this.command<Admission>('portal:commit', {transferId: target.transferId});
            if (gen !== this.generation) return;
            this.adopt(committed);
            const sync = await this.command<{snapshot: Snapshot}>('session:sync');
            if (gen !== this.generation) return;
            this.acceptStream = true; this.snapshot(sync.snapshot);
            this.online = true; this.onStatus(`已进入「${committed.room.name}」`);
        } catch (e) {
            if (gen !== this.generation) return;
            // An ACK can be lost after commit. Ask the server before rolling anything back.
            try {
                let sync = await this.command<{admission: Admission; snapshot: Snapshot; phase: string}>('session:sync');
                if (gen !== this.generation) return;
                if (sync.phase === 'transferring') { await this.command('portal:cancel'); if (gen !== this.generation) return; sync = await this.command('session:sync'); }
                if (gen !== this.generation) return;
                this.acceptStream = false; this.adopt(sync.admission); await this.onLoad(sync.admission);
                if (gen !== this.generation) return;
                this.acceptStream = true; this.snapshot(sync.snapshot); this.online = sync.phase === 'online';
            } catch { if (gen !== this.generation) return; this.online = false; }
            this.onStatus(`传送未完成：${e instanceof Error ? e.message : e}`);
        } finally { if (gen === this.generation) this.busy = false; }
    }
    dispose() { this.stopped = true; clearInterval(this.clockTimer); this.socket.disconnect(); }
}
